import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ClientDetailModal from './ClientDetailModal';

// --- Email Modal Component ---
const EmailModal = ({ client, onClose, onSend }) => {
  const [subject, setSubject] = useState("Regarding your inquiry");
  const [message, setMessage] = useState(
    `Hello ${client?.name},\n\nThank you for reaching out! We will get back to you shortly.`
  );

  if (!client) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
        >
          ✕
        </button>
        <h2 className="text-lg font-semibold mb-4">Send Email</h2>
        <div className="space-y-3">
          <div className="p-3 bg-gray-100 rounded">
            <strong>To:</strong> {client.email}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm focus:ring focus:ring-accent focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Message</label>
            <textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm focus:ring focus:ring-accent focus:outline-none"
            />
          </div>
        </div>

        <div className="flex justify-end mt-5 space-x-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              onSend({ ...client, subject, message });
              onClose();
            }}
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};

const ClientTable = ({
  clients,
  onSendEmail,
  onScheduleCallback,
  onBulkAction,
  selectedClients,
  onClientSelect,
  onSelectAll,
  onDeleteClient
}) => {
  const [detailModalClient, setDetailModalClient] = useState(null);
  const [emailModalClient, setEmailModalClient] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [deleteModalClient, setDeleteModalClient] = useState(null);
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedClients = useMemo(() => {
    if (!sortConfig?.key) return clients;

    return [...clients]?.sort((a, b) => {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [clients, sortConfig]);

  const getSortIcon = (columnKey) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ArrowUpDown" size={14} className="opacity-50" />;
    }
    return sortConfig?.direction === 'asc'
      ? <Icon name="ArrowUp" size={14} />
      : <Icon name="ArrowDown" size={14} />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isAllSelected = selectedClients?.length === clients?.length && clients?.length > 0;
  const isIndeterminate = selectedClients?.length > 0 && selectedClients?.length < clients?.length;

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden lg:block bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="w-12 px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = isIndeterminate;
                    }}
                    onChange={(e) => onSelectAll(e?.target?.checked)}
                    className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-accent focus:ring-2"
                  />
                </th>
                <th className="px-4 py-3 text-center">
                  <button
                    onClick={() => handleSort("name")}
                    className="flex items-center justify-center w-full space-x-2 font-body font-medium text-sm text-foreground hover:text-accent transition-smooth"
                  >
                    <span>Client Name</span>
                    {getSortIcon("name")}
                  </button>
                </th>
                <th className="px-4 py-3 text-center">
                  <button
                    onClick={() => handleSort("email")}
                    className="flex items-center justify-center w-full space-x-2 font-body font-medium text-sm text-foreground hover:text-accent transition-smooth"
                  >
                    <span>Contact Info</span>
                    {getSortIcon("email")}
                  </button>
                </th>
                <th className="px-4 py-3 text-center">
                  <button
                    onClick={() => handleSort("location")}
                    className="flex items-center justify-center w-full space-x-2 font-body font-medium text-sm text-foreground hover:text-accent transition-smooth"
                  >
                    <span>Location</span>
                    {getSortIcon("location")}
                  </button>
                </th>
                <th className="px-4 py-3 text-center">
                  <button
                    onClick={() => handleSort("date")}
                    className="flex items-center justify-center w-full space-x-2 font-body font-medium text-sm text-foreground hover:text-accent transition-smooth"
                  >
                    <span>Event Date</span>
                    {getSortIcon("date")}
                  </button>
                </th>
                <th className="px-4 py-3 text-center">
                  <button
                    onClick={() => handleSort("inquiryDate")}
                    className="flex items-center justify-center w-full space-x-2 font-body font-medium text-sm text-foreground hover:text-accent transition-smooth"
                  >
                    <span>Inquiry Date</span>
                    {getSortIcon("inquiryDate")}
                  </button>
                </th>
                <th className="px-4 py-3 text-center">
                  <span className="font-body font-medium text-sm text-foreground">
                    Service Interest
                  </span>
                </th>
                <th className="px-4 py-3 text-center">
                  <span className="font-body font-medium text-sm text-foreground">
                    Actions
                  </span>
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-border">
              {sortedClients?.map((client) => (
                <tr
                  key={client?.id}
                  className="hover:bg-muted/30 transition-smooth"
                >
                  <td className="px-4 py-4 text-center">
                    <input
                      type="checkbox"
                      checked={selectedClients?.includes(client?.id)}
                      onChange={(e) =>
                        onClientSelect(client?.id, e?.target?.checked)
                      }
                      className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-accent focus:ring-2"
                    />
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
                        <Icon
                          name="User"
                          size={16}
                          color="var(--color-accent)"
                        />
                      </div>
                      <div>
                        <p className="font-body font-medium text-sm text-foreground">
                          {client?.name}
                        </p>
                        <p className="font-body text-xs text-muted-foreground">
                          ID: {client?.id}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="space-y-1">
                      <p className="font-body text-sm text-foreground">
                        {client?.email}
                      </p>
                      <p className="font-body text-xs text-muted-foreground">
                        {client?.phone}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <Icon
                        name="MapPin"
                        size={14}
                        color="var(--color-muted-foreground)"
                      />
                      <span className="font-body text-sm text-foreground">
                        {client?.location}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="font-body text-sm text-foreground">
                      {formatDate(client?.date)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="font-body text-sm text-foreground">
                      {formatDate(client?.inquiryDate)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-body font-medium bg-accent/10 text-accent">
                      {client?.serviceInterest}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        onClick={() => setDetailModalClient(client)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Mail"
                        onClick={() => setEmailModalClient(client)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Phone"
                        onClick={() => onScheduleCallback(client)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => setDeleteModalClient(client)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteModalClient && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50">
            <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-sm w-full animate-fadeIn">
              {/* Warning Icon / Emoji */}
              <div className="flex justify-center mb-4">
                <span className="text-5xl">⚠️</span>
              </div>

              {/* Title */}
              <h2 className="text-xl font-body font-bold text-red-600 text-center mb-2">
                Delete Confirmation
              </h2>

              {/* Message */}
              <p className="text-center text-sm text-muted-foreground mb-6">
                Are you sure you want to permanently delete{" "}
                <span className="font-semibold text-foreground">
                  {deleteModalClient.name}
                </span>
                ? <br />
                This action cannot be undone.
              </p>

              {/* Buttons */}
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setDeleteModalClient(null)}
                  className="px-5 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition"
                >
                  ❌ Cancel
                </button>
                <button
                  onClick={() => {
                    onDeleteClient(deleteModalClient.id);
                    setDeleteModalClient(null);
                  }}
                  className="px-5 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 shadow-sm transition"
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Mobile Cards - Fixed Layout */}
      <div className="lg:hidden space-y-4">
        {sortedClients?.map((client) => (
          <div
            key={client?.id}
            className="bg-card rounded-lg border border-border p-4"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedClients?.includes(client?.id)}
                  onChange={(e) =>
                    onClientSelect(client?.id, e?.target?.checked)
                  }
                  className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-accent focus:ring-2 mt-1"
                />
                <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="User" size={16} color="var(--color-accent)" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-body font-medium text-sm text-foreground truncate">
                    {client?.name}
                  </p>
                  <p className="font-body text-xs text-muted-foreground">
                    ID: {client?.id?.substring(0, 8)}...
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                iconName="Trash2"
                onClick={() => setDeleteModalClient(client)}
                className="text-red-500 hover:text-red-700 flex-shrink-0"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 mb-4">
              <div className="flex items-start space-x-2">
                <Icon
                  name="Mail"
                  size={14}
                  color="var(--color-muted-foreground)"
                  className="mt-0.5 flex-shrink-0"
                />
                <span className="font-body text-sm text-foreground break-all">
                  {client?.email}
                </span>
              </div>

              {client?.phone && (
                <div className="flex items-center space-x-2">
                  <Icon
                    name="Phone"
                    size={14}
                    color="var(--color-muted-foreground)"
                    className="flex-shrink-0"
                  />
                  <span className="font-body text-sm text-foreground">
                    {client?.phone}
                  </span>
                </div>
              )}

              {client?.location && (
                <div className="flex items-center space-x-2">
                  <Icon
                    name="MapPin"
                    size={14}
                    color="var(--color-muted-foreground)"
                    className="flex-shrink-0"
                  />
                  <span className="font-body text-sm text-foreground">
                    {client?.location}
                  </span>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Icon
                  name="Calendar"
                  size={14}
                  color="var(--color-muted-foreground)"
                  className="flex-shrink-0"
                />
                <div>
                  <span className="font-body text-xs text-muted-foreground block">
                    Event:
                  </span>
                  <span className="font-body text-sm text-foreground">
                    {formatDate(client?.date)}
                  </span>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Icon
                  name="Clock"
                  size={14}
                  color="var(--color-muted-foreground)"
                  className="flex-shrink-0"
                />
                <div>
                  <span className="font-body text-xs text-muted-foreground block">
                    Inquiry:
                  </span>
                  <span className="font-body text-sm text-foreground">
                    {formatDate(client?.inquiryDate)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-border">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-body font-medium bg-accent/10 text-accent">
                {client?.serviceInterest}
              </span>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Eye"
                  onClick={() => setDetailModalClient(client)}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Mail"
                  onClick={() => setEmailModalClient(client)}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Phone"
                  onClick={() => onScheduleCallback(client)}
                />
              </div>
            </div>
          </div>
        ))}

        {/* Delete Confirmation Modal (Mobile + Desktop shared) */}
        {deleteModalClient && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50">
            <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-sm w-full animate-fadeIn">
              <div className="flex justify-center mb-4">
                <span className="text-5xl">⚠️</span>
              </div>
              <h2 className="text-xl font-body font-bold text-red-600 text-center mb-2">
                Delete Confirmation
              </h2>
              <p className="text-center text-sm text-muted-foreground mb-6">
                Are you sure you want to permanently delete{" "}
                <span className="font-semibold text-foreground">
                  {deleteModalClient.name}
                </span>
                ? <br />
                This action cannot be undone.
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setDeleteModalClient(null)}
                  className="px-5 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition"
                >
                  ❌ Cancel
                </button>
                <button
                  onClick={() => {
                    onDeleteClient(deleteModalClient.id);
                    setDeleteModalClient(null);
                  }}
                  className="px-5 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 shadow-sm transition"
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Client Detail Modal */}
      {detailModalClient && (
        <ClientDetailModal
          client={detailModalClient}
          onClose={() => setDetailModalClient(null)}
        />
      )}

      {/* Email Modal */}
      {emailModalClient && (
        <EmailModal
          client={emailModalClient}
          onClose={() => setEmailModalClient(null)}
          onSend={onSendEmail}
        />
      )}
    </>
  );
};

export default ClientTable;